import React, { memo, Fragment, useState, useEffect, useRef } from 'react'
import { useForm } from '../../utils/form-hook'
import { usePasswordValidation } from '../../utils/password-validation-hook'
import { usePhoneValidation } from '../../utils/phone-validation-hook'
import { gql, useMutation } from '@apollo/client'
import { CORE_USER_FIELDS } from '../../graphql/user-fragments'
import { Form, Button, Alert, Spinner, Image } from 'react-bootstrap'
import Card from '../common/Card'
import PhoneFormInput from '../common/PhoneFormInput'
import ShowErrors from '../partials/ShowErrors'
import _ from 'lodash'
import { ProfileIcon } from '../icons/new'
import PasswordInput from '../common/PasswordInput'

const UPDATE_USER = gql`
    ${CORE_USER_FIELDS}
    mutation UpdateUser($id: ID!, $userInput: UserInput) {
        updateUser(id: $id, userInput: $userInput) {
            ...CoreUserFields
        }
    }
`

const UserProfile = memo(({ updateUserData, data, user, validate, callback }) => {
    const [errors, setError] = useState([])
    const [success, setSuccess] = useState(false)
    const submitRef = useRef(null)

    const updateUserCallback = async () => {
        await updateUser()
    }

    const initialValues = {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        password: '',
        confirmPassword: '',
        logoImage: null,
        // TODO update user mutation to exclude these required props;
        address: '',
        businessName: '',
        timeZoneOffset: 0,
    }

    if (data?.users && data?.users.length > 0) {
        _.each(initialValues, (value, key) => {
            if (data.users[0][key] != null && key !== 'logoImage') {
                initialValues[key] = data.users[0][key]
            }
        })
    }

    const { onChange, onFileChange, onSubmit, values, validated, setValue, validateEmail, resetValidation } = useForm(
        updateUserCallback,
        initialValues,
    )

    const { passwordValidation } = usePasswordValidation(values, true)
    const { isPhoneValid, phoneValidation } = usePhoneValidation(values)

    useEffect(() => {
        phoneValidation()
    }, [values.phone, phoneValidation])

    useEffect(() => {
        updateUserData(values)
    }, [values, updateUserData])

    useEffect(() => {
        if (submitRef.current && validate) {
            submitRef.current.click()
        }
    }, [validate])

    const [updateUser, mutation] = useMutation(UPDATE_USER, {
        onError({ graphQLErrors }) {
            setError(graphQLErrors)
        },
        onCompleted: () => {
            resetValidation()
            setError([])
            setSuccess(true)
            callback()
        },
        variables: {
            id: user.id,
            userInput: values,
        },
    })

    return (
        <Fragment>
            <Card className="page-card">
                <Card.Header className="d-flex justify-content-between">
                    <div className="header-title d-flex align-items-center">
                        <i className="icon text-gray-200 me-1">
                            <ProfileIcon size={24} />
                        </i>
                        <h5 className="mb-0 text-white">Profile</h5>
                    </div>
                </Card.Header>
                <Card.Body>
                    <div className="new-user-info">
                        <Form noValidate validated={validated}>
                            <h5 className="mb-3">Campaign Manager</h5>
                            <div className="row">
                                <Form.Group className="col-md-4 form-group">
                                    <Form.Label htmlFor="fname" className="tw:text-sm">
                                        First Name
                                    </Form.Label>
                                    <Form.Control
                                        required
                                        type="text"
                                        id="fname"
                                        placeholder="Enter First Name"
                                        name="firstName"
                                        value={values.firstName}
                                        onChange={onChange}
                                    ></Form.Control>
                                </Form.Group>
                                <Form.Group className="col-md-4 form-group">
                                    <Form.Label htmlFor="lname" className="tw:!text-sm">
                                        Last Name
                                    </Form.Label>
                                    <Form.Control
                                        required
                                        type="text"
                                        id="lname"
                                        placeholder="Enter Last Name"
                                        name="lastName"
                                        value={values.lastName}
                                        onChange={onChange}
                                    ></Form.Control>
                                </Form.Group>
                                <Form.Group className="col-md-4 form-group step-1">
                                    <Form.Label htmlFor="mobno" className="tw:!text-sm">
                                        Phone Number
                                    </Form.Label>
                                    <PhoneFormInput
                                        value={values.phone}
                                        setValue={setValue}
                                        validated={validated}
                                        isValid={isPhoneValid}
                                    />
                                    {validated && !isPhoneValid && (
                                        <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                                            Phone number is invalid
                                        </Form.Control.Feedback>
                                    )}
                                </Form.Group>
                            </div>
                            <hr />
                            <h5 className="mb-3">Login Details</h5>
                            <div className="row">
                                <Form.Group className="col-md-4 form-group">
                                    <Form.Label htmlFor="email" className="tw:!text-sm">
                                        Email
                                    </Form.Label>
                                    <Form.Control
                                        required
                                        type="email"
                                        id="email"
                                        placeholder="Enter Email"
                                        name="email"
                                        value={values.email}
                                        onChange={onChange}
                                        onKeyUp={validateEmail}
                                    ></Form.Control>
                                    <Form.Control.Feedback type="invalid">
                                        {values.email.length > 0 ? 'Please enter a valid Email' : ''}
                                    </Form.Control.Feedback>
                                </Form.Group>
                                <Form.Group className="col-md-4 form-group">
                                    <Form.Label htmlFor="password" className="tw:!text-sm">
                                        Password
                                    </Form.Label>
                                    <PasswordInput
                                        id="password"
                                        placeholder="Leave empty to not update"
                                        name="password"
                                        onChange={onChange}
                                        onKeyUp={passwordValidation}
                                    >
                                        <Form.Control.Feedback type="invalid">
                                            {values.password.length > 0 ? 'Weak Password' : ''}
                                        </Form.Control.Feedback>
                                    </PasswordInput>
                                </Form.Group>
                                <Form.Group className="col-md-4 form-group">
                                    <Form.Label htmlFor="confirmPassword" className="tw:!text-sm">
                                        Repeat Password
                                    </Form.Label>
                                    <PasswordInput
                                        id="confirmPassword"
                                        placeholder="Leave empty to not update"
                                        name="confirmPassword"
                                        onChange={onChange}
                                        onKeyUp={passwordValidation}
                                    >
                                        <Form.Control.Feedback type="invalid">
                                            {values.password !== values.confirmPassword ? 'Passwords do not match' : ''}
                                        </Form.Control.Feedback>
                                    </PasswordInput>
                                </Form.Group>
                            </div>
                            <hr />
                            <h5 className="mb-3">Your Logo</h5>
                            <div className="row">
                                <Form.Group className="form-group">
                                    <Form.Label className="custom-file-input tw:!text-sm">Update Logo</Form.Label>
                                    {/* TODO: Replace with ThumbnailUploader component */}
                                    <Form.Control
                                        type="file"
                                        accept=".jpg,.gif,.png"
                                        id="logo-image"
                                        name="logoImage"
                                        onChange={onFileChange}
                                    />
                                </Form.Group>
                            </div>
                            <div className="pb-3">
                                <ShowErrors errors={errors} />
                                <Alert
                                    variant="success alert-left alert-dismissible fade show mb-3"
                                    role="alert"
                                    show={success}
                                    onClose={() => setSuccess(false)}
                                    dismissible
                                >
                                    <span> User Profile has been successfully updated </span>
                                </Alert>
                            </div>
                            <div className="d-flex justify-content-end border-top pt-4 tw:!-mx-[1.5rem] tw:!px-[1.5rem]">
                                <Button
                                    type="button"
                                    variant="btn btn-primary"
                                    onClick={onSubmit}
                                    disabled={mutation.loading}
                                    ref={submitRef}
                                    className="step-3"
                                >
                                    {mutation.loading ? (
                                        <Fragment>
                                            <Spinner
                                                as="span"
                                                animation="grow"
                                                size="sm"
                                                role="status"
                                                aria-hidden="true"
                                            />{' '}
                                            Saving...
                                        </Fragment>
                                    ) : (
                                        <Fragment>Save</Fragment>
                                    )}
                                </Button>
                            </div>
                        </Form>
                    </div>
                </Card.Body>
            </Card>
        </Fragment>
    )
})

UserProfile.displayName = 'UserProfile'
export default UserProfile
